// An SLO rule's page parts (D119; Console D's #/alerts/slo; APS-279 step 4b's contract v1): the
// strip (the budget left, the page pair's burn, when the budget runs out, the newest release
// alone), the burn-rate windows (each pair against its rate, and the at-risk line), the routing
// per severity, and what fired, at risk and resolved, per pair. Every number is the evaluator's
// last measurement, kept in Postgres: the page waits on no ClickHouse.
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { AlertDestination, AlertEvent, AlertRule, AlertSlo, SloWindow } from '../api/types'
import { KV, Status, type StripItem, Table } from '../components/d'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { DELIVERY } from '../lib/alerts'
import { lasted } from '../lib/format'
import { formatBurn, leftPercent, spentAtRate } from '../lib/slo'
import { useProjectParams } from './params'

const WINDOW: Record<SloWindow, MessageId> = {
  '5m': 'alerts.slo.w.5m',
  '30m': 'alerts.slo.w.30m',
  '1h': 'alerts.slo.w.1h',
  '6h': 'alerts.slo.w.6h',
  '3d': 'alerts.slo.w.3d',
}

const day = (i18n: Translator, iso: string) => i18n.date(iso, { month: 'short', day: 'numeric' })
const dateTime = (i18n: Translator, iso: string) =>
  i18n.date(iso, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
/** A crash-free share as the mock writes it: 99.38 %. */
const crashFree = (i18n: Translator, share: number) =>
  `${i18n.number(share * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
const percent = (i18n: Translator, share: number) =>
  `${i18n.number(Math.max(0, share) * 100, { maximumFractionDigits: 0 })} %`

/** The strip of an SLO rule's page. */
export function sloStrip(rule: AlertRule, i18n: Translator): StripItem[] {
  const { t, number } = i18n
  const slo = rule.slo
  if (!slo || slo.measured_at === null)
    return [
      {
        dt: t('alerts.slo.budgetLeft'),
        dd: '—',
        // A rule off is not checked; one on, or just started over, is within a minute.
        d: rule.enabled ? t('alerts.slo.notMeasuredYet') : t('alerts.slo.notMeasuredOff'),
      },
    ]
  const left = leftPercent(slo.budget.left)
  const page = slo.pairs.find((p) => p.pair === 'page')
  const items: StripItem[] = [
    {
      dt: t('alerts.slo.budgetLeft'),
      dd: left ? number(left.value, { maximumFractionDigits: 0 }) : '—',
      small: left ? '%' : undefined,
      d: !left ? (
        t('alerts.slo.notEnough')
      ) : left.overspent ? (
        <span className="c-crash">{t('alerts.slo.overspent')}</span>
      ) : slo.at_risk.state === 'at_risk' ? (
        <>
          {/* Parts that wrap whole: two of them fit half a 390 px row (2 × 2). */}
          <span className="c-warn">{t('alerts.slo.under', { n: slo.at_risk.below })}</span>
          {slo.at_risk.since ? (
            <>
              {' · '}
              {t('alerts.slo.since', { day: day(i18n, slo.at_risk.since) })}
            </>
          ) : null}
        </>
      ) : (
        t('alerts.slo.atRiskUnder', { n: slo.at_risk.below })
      ),
    },
    {
      dt: t('alerts.slo.burn1h'),
      dd:
        page?.burn_long === null || page === undefined
          ? '—'
          : number(page.burn_long, { maximumFractionDigits: 2 }),
      small: page && page.burn_long !== null ? '×' : undefined,
      d: page ? t('alerts.slo.pagesAt', { rate: formatBurn(i18n, page.rate) }) : undefined,
    },
    {
      dt: t('alerts.slo.runsOut'),
      dd: slo.exhausts_at ? day(i18n, slo.exhausts_at) : '—',
      d:
        slo.exhausts_at && slo.exhausts_burn !== null
          ? t('alerts.slo.at6h', { burn: formatBurn(i18n, slo.exhausts_burn) })
          : t('alerts.slo.notSpent'),
    },
  ]
  const latest = slo.latest_release
  if (latest)
    items.push({
      dt: t('alerts.slo.alone', { release: latest.release }),
      dd: number(latest.burn, { maximumFractionDigits: 2 }),
      small: '×',
      // Counted since it shipped, or over the window for a release older than it (contract v2).
      d:
        latest.counted_from === latest.since
          ? t('alerts.slo.releaseSince', {
              crashFree: crashFree(i18n, latest.crash_free),
              day: day(i18n, latest.since),
            })
          : t('alerts.slo.releaseOver', {
              crashFree: crashFree(i18n, latest.crash_free),
              days: slo.window_days,
            }),
    })
  return items
}

/** The chart's subtitle: `28-day rolling · daily · 99.69 % crash-free so far`. */
export function sloChartMeta(slo: AlertSlo | null | undefined, i18n: Translator): string {
  const { t } = i18n
  if (!slo) return t('alerts.slo.daily')
  return [
    t('alerts.slo.rolling', { days: slo.window_days }),
    t('alerts.slo.daily'),
    ...(slo.crash_free === null
      ? []
      : [t('alerts.slo.soFar', { crashFree: crashFree(i18n, slo.crash_free) })]),
  ].join(' · ')
}

/** A destination by name, linked to the settings that edit it (Console D's group links). */
function useDestinationName(destinations: AlertDestination[]) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (id: string | null): ReactNode => {
    const d = id === null ? undefined : destinations.find((x) => x.id === id)
    if (!d) return t('settings.alerts.noDestination')
    return (
      <Link
        className="ul"
        to="/o/$orgId/p/$projectId/settings/$section"
        params={{ orgId, projectId, section: 'alerts' }}
        translate="no"
      >
        {d.name}
      </Link>
    )
  }
}

/** Each pair against its rate, and the at-risk line (the mock's "Burn-rate windows"). */
export function SloBurnTable({
  slo,
  destinations,
}: {
  slo: AlertSlo
  destinations: AlertDestination[]
}) {
  const i18n = useI18n()
  const { t, number } = i18n
  const name = useDestinationName(destinations)
  const left = leftPercent(slo.budget.left)
  return (
    <Table label={t('alerts.slo.burnTableRegion')} className="al-tbl">
      <thead>
        <tr>
          <th scope="col">{t('alerts.slo.col.severity')}</th>
          <th scope="col">{t('alerts.slo.col.windows')}</th>
          <th scope="col" className="num">
            {t('alerts.slo.col.burn')}
          </th>
          <th scope="col" className="num" title={t('alerts.slo.thresholdHelp')}>
            {t('alerts.col.threshold')}
          </th>
          <th scope="col">{t('settings.alerts.status')}</th>
          <th scope="col">{t('alerts.slo.col.routes')}</th>
        </tr>
      </thead>
      <tbody>
        {slo.pairs.map((p) => (
          <tr key={p.pair}>
            <td>{t(p.severity === 'page' ? 'alerts.slo.sev.page' : 'alerts.slo.sev.ticket')}</td>
            <td className="wrap">
              {t('alerts.slo.windows', { long: t(WINDOW[p.long]), short: t(WINDOW[p.short]) })}
            </td>
            <td className="num wrap" translate="no">
              {p.burn_long === null ? '—' : <b>{formatBurn(i18n, p.burn_long)}</b>}
              {p.burn_short === null ? null : (
                <span className="t2">
                  {t('alerts.slo.short', { burn: formatBurn(i18n, p.burn_short) })}
                </span>
              )}
            </td>
            <td className="num wrap" translate="no">
              {formatBurn(i18n, p.rate)}
              <span className="t2">
                {t('alerts.slo.spent', {
                  pct: number(spentAtRate(p.rate, p.long, slo.window_days), {
                    maximumFractionDigits: 1,
                  }),
                  window: t(WINDOW[p.long]),
                })}
              </span>
            </td>
            <td>
              {p.burn_long === null ? (
                <Status s="mute" label={t('alerts.slo.notMeasured')} />
              ) : p.state === 'firing' ? (
                <Status s="fire" label={t('settings.alerts.state.firing')} />
              ) : (
                <Status s="ok" label={t('settings.alerts.state.ok')} />
              )}
            </td>
            <td className="wrap">{name(p.routes_to)}</td>
          </tr>
        ))}
        <tr>
          <td>{t('alerts.slo.sev.ticket')}</td>
          <td className="wrap">{t('alerts.slo.budgetWindow', { days: slo.window_days })}</td>
          <td className="num" translate="no">
            {left ? <b>{`${number(left.value, { maximumFractionDigits: 0 })} %`}</b> : '—'}
          </td>
          <td className="num" translate="no">
            {t('alerts.slo.below', { n: slo.at_risk.below })}
          </td>
          <td>
            {slo.budget.left === null ? (
              // Under min_sessions, as the pairs read (Loom's N3).
              <Status s="mute" label={t('alerts.slo.notMeasured')} />
            ) : slo.at_risk.state === 'at_risk' ? (
              <Status s="reg" label={t('settings.alerts.state.atRisk')} />
            ) : (
              <Status s="ok" label={t('settings.alerts.state.ok')} />
            )}
          </td>
          <td className="wrap">{name(slo.at_risk_routes_to)}</td>
        </tr>
      </tbody>
    </Table>
  )
}

/** Where each severity goes (the mock's "Routing, per threshold"). */
export function SloRouting({
  slo,
  destinations,
}: {
  slo: AlertSlo
  destinations: AlertDestination[]
}) {
  const i18n = useI18n()
  const { t } = i18n
  const name = useDestinationName(destinations)
  return (
    <KV
      label={t('alerts.slo.routing')}
      items={[
        ...slo.pairs.map(
          (p) =>
            [
              t('alerts.slo.route', {
                severity: p.severity,
                rate: formatBurn(i18n, p.rate),
                windows: t('alerts.slo.windows', {
                  long: t(WINDOW[p.long]),
                  short: t(WINDOW[p.short]),
                }),
              }),
              name(p.routes_to),
            ] as const,
        ),
        [
          t('alerts.slo.routeAtRisk', { n: slo.at_risk.below }),
          name(slo.at_risk_routes_to),
        ] as const,
      ]}
    />
  )
}

/** A firing or an at-risk spell of one pair, and what ended it. */
export interface SloRun {
  start: AlertEvent
  end?: AlertEvent
}

/**
 * The SLO's fired and at-risk spells, newest first: each `fired` ends at the next `resolved` of
 * the same pair (APS-279 v1: detail.pair, else the severity), each `at_risk` at the next
 * `at_risk_cleared`.
 */
export function sloRuns(events: AlertEvent[]): SloRun[] {
  const asc = [...events].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
  const open = new Map<string, SloRun>()
  const out: SloRun[] = []
  const key = (e: AlertEvent) =>
    e.kind === 'at_risk' || e.kind === 'at_risk_cleared'
      ? 'at_risk'
      : typeof e.detail.pair === 'string'
        ? e.detail.pair
        : (e.severity ?? 'page')
  for (const e of asc) {
    if (e.kind === 'fired' || e.kind === 'at_risk') {
      const run: SloRun = { start: e }
      out.push(run)
      open.set(key(e), run)
    } else if (e.kind === 'resolved' || e.kind === 'at_risk_cleared') {
      const run = open.get(key(e))
      if (run) {
        run.end = e
        open.delete(key(e))
      }
    }
  }
  return out.reverse()
}

/**
 * Whether a spell with no end still goes on: its pair still fires, or the rule is still at risk.
 * One the API started over, or a pair turned off, ended with no message (APS-279 4a).
 */
function stillOn(start: AlertEvent, slo: AlertSlo | null): boolean {
  if (!slo) return true
  if (start.kind === 'at_risk') return slo.at_risk.state === 'at_risk'
  const pair = typeof start.detail.pair === 'string' ? start.detail.pair : start.severity
  return slo.pairs.some((p) => p.pair === pair && p.state === 'firing')
}

/** What fired, went at risk and resolved (the mock's "Fired" table for an SLO). */
export function SloFired({
  runs,
  now,
  slo,
}: {
  runs: SloRun[]
  now: number
  slo: AlertSlo | null
}) {
  const i18n = useI18n()
  const { t } = i18n
  return (
    <Table label={t('alerts.firedTable')} className="al-tbl">
      <thead>
        <tr>
          <th scope="col">{t('alerts.col.when')}</th>
          <th scope="col">{t('alerts.slo.col.event')}</th>
          <th scope="col">{t('alerts.col.valueAt')}</th>
          <th scope="col">{t('releases.col.release')}</th>
          <th scope="col">{t('alerts.col.lasted')}</th>
          <th scope="col">{t('settings.alerts.delivery')}</th>
        </tr>
      </thead>
      <tbody>
        {runs.map(({ start, end }) => {
          const d = DELIVERY[start.delivery]
          const risk = start.kind === 'at_risk'
          const burn = start.detail.burn_long
          const left = start.detail.budget_left
          const release = typeof start.detail.release === 'string' ? start.detail.release : ''
          return (
            <tr key={start.id}>
              <td>{dateTime(i18n, start.created_at)}</td>
              <td>
                {risk ? (
                  <Status s="reg" label={t('settings.alerts.state.atRisk')} />
                ) : (
                  <Status
                    s="fire"
                    label={t('alerts.slo.firedAs', {
                      severity: start.severity === 'page' ? 'page' : 'ticket',
                    })}
                  />
                )}
              </td>
              <td translate="no">
                {risk
                  ? typeof left === 'number'
                    ? t('alerts.slo.leftValue', { left: percent(i18n, left) })
                    : '—'
                  : typeof burn === 'number'
                    ? formatBurn(i18n, burn)
                    : '—'}
              </td>
              <td translate="no">{release || <span className="faint">—</span>}</td>
              <td>
                {end ? (
                  lasted(i18n, Date.parse(end.created_at) - Date.parse(start.created_at))
                ) : !stillOn(start, slo) ? (
                  <span className="faint">—</span>
                ) : (
                  <span className={risk ? 'c-warn' : 'c-crash'}>
                    {t('alerts.ongoing')}{' '}
                    <span className="faint">
                      · {lasted(i18n, Math.max(0, now - Date.parse(start.created_at)))}
                    </span>
                  </span>
                )}
              </td>
              <td>
                <Status s={d.s} label={t(d.label)} />
                {start.error_code ? (
                  <span className="t2" translate="no">
                    {start.error_code}
                  </span>
                ) : null}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
